import { useMutation, useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { v4 as uuid } from "uuid";

import { ShipmentChangeKeys } from "../components/Booking/util";
import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { useShipmentContext } from "../components/Shipments/hooks/useShipmentContext";
import { PRIVATE_ROUTES } from "../constants";
import { useAppState } from "../context/StateContext";
import { ContainerPosition, ContainerStatus, Event, Location } from "../models/marine.traffic";
import { Filters, Searches } from "../models/shipeezi.table";
import { FileInput, ShipmentDocument } from "../models/shipment.document";
import { Shipment, ReceivedProduct } from "../models/shipments";
import MarineTrafficService from "../services/marine.traffic.service";
import ShipmentContainerService from "../services/shipment.container.service";
import ShipmentDocumentService from "../services/shipment.document.service";
import ShipmentService from "../services/shipment.service";
import { keysToCamel, keysToSnake } from "../util/convertCamelCase";
import { ShipmentStatus, ShipmentType } from "../util/enums";
import { BookingKeys, DocumentKeys, ShipmentHistoriesKeys, ShipmentKeys } from "../util/queries";
import ShipmentHistoryService from "Services/shipment.history.service";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

interface SaveShipmentProps {
  id?: number;
  shipment: Shipment;
  marineTrafficId?: string;
  changeOptionsNew?: ShipmentChangeKeys;
}

interface UpdateShipmentFromMarineStatusProps {
  shipment: Shipment;
  containerStatus: ContainerStatus;
  departureLocation?: Location;
  containerAtPOLEvent?: Event;
}

interface ImportShipmentProps {
  file: any;
  userId: string;
}

const useActionShipment = () => {
  const queryClient = useQueryClient();
  const { shipment, setShipment, setRootContainerId, changedOptions } = useShipmentContext();
  const { currentUser } = useAppState();

  const FM_VERSION: number = 2;
  const isFmVersion: boolean = currentUser?.company.profileId === FM_VERSION;
  const projectId = currentUser?.projects[0]?.id ?? 0;
  const eventType = currentUser?.projects[0]?.name ?? "";

  const { t } = useTranslation();

  const history = useHistory();

  const getAllShipments = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: ShipmentKeys.getAll([page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await ShipmentService.getAll({
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify([
            { association: "milestones", include: [{ association: "milestone" }] },
            {
              association: "shipmentTracking",
              include: [{ association: "vesselPosition" }],
            },
            { association: "consignee" },
          ]),
          isBooking: false,
          ...filters,
        });

        if (response && response.data) {
          return response.data;
        }

        return [];
      },
      enabled: !!currentUser?.id,
      cacheTime: CACHE_TIME,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    });
  };

  const getAllShipmentsCount = (searches?: Searches, filters?: Filters) => {
    return useQuery({
      queryKey: ShipmentKeys.count([searches, filters]),
      queryFn: async () => {
        const response = await ShipmentService.count({
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          isBooking: false,
          ...filters,
        });

        if (response && response.data) {
          return response.data;
        }

        return 0;
      },
      enabled: !!currentUser?.id,
      cacheTime: CACHE_TIME,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    });
  };

  const getShipmentById = (shipmentId: number) => {
    return useQuery(
      ["shipment.get", shipmentId],
      () =>
        ShipmentService.getById(Number(shipmentId), {
          include: JSON.stringify([
            { association: "shipperEntity" },
            {
              association: "portOfLoading",
              attributes: ["siteCode", "locationName"],
              include: [{ association: "country", attributes: ["code"] }],
            },
            {
              association: "portOfDischarge",
              attributes: ["siteCode", "locationName"],
              include: [{ association: "country", attributes: ["code"] }],
            },
            {
              association: "portOfDestination",
              attributes: ["siteCode", "locationName"],
              include: [{ association: "country", attributes: ["code"] }],
            },
            { association: "consignee", attributes: ["code", "fullName", "name", "lastName"] },
            { association: "containers" },
            {
              association: "commodityCode",
              attributes: ["id", "hscode", "description"],
            },
            { association: "originLocation" },
            { association: "destinationLocation" },
            {
              association: "shipmentTracking",
              include: [{ association: "vesselPosition" }],
            },
          ]),
        }),
      {
        enabled: !!shipmentId,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) {
            const shipmentCamelCase = response.data;

            setShipment((prev) => ({
              ...prev,
              ...shipmentCamelCase,
              containers: shipmentCamelCase.containers?.map((container) => ({
                ...container,
                uuid: uuid(),
              })),
            }));

            setRootContainerId(shipmentCamelCase?.containerId || "");
          }
        },
      }
    );
  };

  const getExportShipmentsById = (
    setShipments: React.Dispatch<React.SetStateAction<Shipment[]>>,
    shipmentIds: string[] | number[]
  ) => {
    return useQueries({
      queries: shipmentIds.map((shipmentId) => {
        return {
          queryKey: ["shipment.get", shipmentId],
          queryFn: () => ShipmentService.getExportById(Number(shipmentId)),
          enabled: !!shipmentId,
          refetchOnWindowFocus: false,
          onSuccess: (response: AxiosResponse<Shipment[]>) => {
            if (response && response.data) {
              setShipments((shipments) => [...shipments, keysToCamel(response.data[0])]);
            }
          },
        };
      }),
    });
  };

  const getShipmentsById = (
    setShipments: React.Dispatch<React.SetStateAction<Shipment[]>>,
    shipmentIds: string[] | number[]
  ) => {
    return useQueries({
      queries: shipmentIds.map((shipmentId) => {
        return {
          queryKey: ["shipment.get", shipmentId],
          queryFn: () => ShipmentService.getById(Number(shipmentId)),
          enabled: !!shipmentId,
          refetchOnWindowFocus: false,
          onSuccess: (response: AxiosResponse<Shipment>) => {
            if (response && response.data) {
              setShipments((shipments) => [...shipments, keysToCamel(response.data)]);
            }
          },
        };
      }),
    });
  };

  const getShipmentHistories = (shipmentId?: number) => {
    return useQuery({
      queryKey: ShipmentHistoriesKeys.getAll([shipmentId]),

      queryFn: async () => {
        const response = await ShipmentHistoryService.getAll({
          shipmentId,
        });

        if (response?.data) return response?.data;

        return [];
      },
      enabled: !!shipmentId,
    });
  };

  const getReceivedProducts = (
    setReceivedProducts: (receivedProducts: ReceivedProduct[]) => void,
    containerId: string
  ) => {
    return useQuery(
      ["shipment.get.received.products", containerId],
      () => ShipmentService.getReceivedProducts(containerId),
      {
        enabled: !!containerId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) {
            const camelResponse = keysToCamel(response.data);

            // Creates an artificial id for the products and filters the
            // scanned ones
            const latestProductsScan = camelResponse
              .map((product: ReceivedProduct, index: number) => ({
                ...product,
                artificialId: index,
              }))
              .filter(
                (product: ReceivedProduct) => product.exception === 0 && product.conferenceId > 0
              );

            setReceivedProducts(latestProductsScan);
          }
        },
      }
    );
  };

  const useImportShipment = () => {
    return useMutation(
      ["shipment.import"],
      ({ file, userId }: ImportShipmentProps) => ShipmentService.import(file, userId),
      {
        onSuccess: () => {},
      }
    );
  };

  const useExportShipment = () => {
    return useMutation(["shipment.export"], (shipId: number) => ShipmentService.export(shipId), {
      onSuccess: (response) => {
        if (response && response.data) {
          const { data } = response;
          const filename = `${new Date().toISOString()}`;
          const extension = "json";
          const fileNameWithExtension = `${filename}.${extension}`;

          const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
            JSON.stringify(data)
          )}`;
          const link = document.createElement("a");

          link.href = jsonString;
          link.download = fileNameWithExtension;
          document.body.appendChild(link); // required for firefox

          link.click();
          link.remove();
        }
      },
    });
  };

  const useSaveOldShipment = () => {
    return useMutation(
      ["shipment.save"],
      ({ id, shipment, marineTrafficId }: SaveShipmentProps) => {
        if (id)
          return ShipmentService.updateV2(
            keysToSnake({
              ...shipment,
              company_id: currentUser?.companyId,
            })
          );

        return ShipmentService.createV2(
          keysToSnake({
            ...shipment,
            company_id: currentUser?.companyId,
            marine_traffic_id: marineTrafficId,
            project_id: isFmVersion ? shipment.projectId : projectId,
            event_type: isFmVersion ? shipment.eventType : eventType,
          })
        );
      },
      {
        onSuccess: () => {
          NotificationToast.success(t("operationPerformedSuccessfully"));
          history.push(PRIVATE_ROUTES.SHIPMENTS);
        },
      }
    );
  };

  const useSaveShipment = (callback?: () => void) => {
    return useMutation(
      ["shipment.save"],
      ({ id, shipment, marineTrafficId, changeOptionsNew }: SaveShipmentProps) => {
        if (id)
          return ShipmentService.update(id, {
            record: shipment,
            changedOptions: changeOptionsNew || changedOptions,
          });

        return ShipmentService.create({
          ...shipment,
          companyId: currentUser?.companyId,
          marineTrafficId,
          projectId: isFmVersion ? shipment.projectId : projectId,
          eventType: isFmVersion ? shipment.eventType : eventType,
        });
      },
      {
        onSuccess: () => {
          NotificationToast.success(t("operationPerformedSuccessfully"));
          queryClient.invalidateQueries({ queryKey: [ShipmentKeys.all] });
          queryClient.invalidateQueries({ queryKey: [BookingKeys.all] });
          if (callback) callback();
        },
      }
    );
  };

  const useUpdateShipmentFromMarineStatus = (refetchShipment?: () => void) => {
    return useMutation(
      ["shipment.udpate.marine.status"],
      ({
        shipment,
        containerStatus,
        departureLocation,
        containerAtPOLEvent,
      }: UpdateShipmentFromMarineStatusProps) => {
        return ShipmentService.updateV2(
          keysToSnake({
            ...shipment,
            origin: departureLocation?.locationName ?? shipment.origin,
            destination: containerStatus.finalPortName ?? shipment.destination,
            vesselName: containerStatus.vesselName ?? shipment.vesselName,
            etaFinalPort: containerStatus.finalPortPredictiveArrivalUtc ?? shipment.etaFinalPort,
            arriveDc: containerStatus.finalPortActualArrivalUtc ?? shipment.arriveDc,
            actualDateDeparture: containerAtPOLEvent?.eventDatetime ?? shipment.actualDateDeparture,
          })
        );
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["shipment.get"]);
          if (refetchShipment) refetchShipment();
          NotificationToast.success(t("voyageStatusUpdated"));
        },
      }
    );
  };

  // SE-1496: "Cancel Shipment" intentionally reuses shipmentPermission.delete as its
  // permission gate. The destructive end-state (shipment becomes unusable for the
  // operator) is equivalent to the old hard-delete, so a new permission key would
  // only fragment ACLs without adding security. Revisit if product needs a finer split.
  const useCancelShipment = () => {
    return useMutation(
      ["shipment.cancel"],
      (id: number) =>
        ShipmentService.update(Number(id), {
          record: { statusShipment: ShipmentStatus.CANCELLED, marineTrafficId: null, shipmentTrackingId: null },
          changedOptions: {},
        }),
      {
        onSuccess: () => {
          NotificationToast.success(t("operationPerformedSuccessfully"));
          history.push(PRIVATE_ROUTES.SHIPMENTS);
        },
      }
    );
  };

  const useDeleteShipmentContainer = (reload?: boolean) => {
    return useMutation(
      ["shipment.container.delete"],
      (id: number) => ShipmentContainerService.delete(id),
      {
        onSuccess: () => {
          NotificationToast.success(t("theProductWasRemovedSuccesfully"));
          if (reload) history.go(0);
          else history.push(PRIVATE_ROUTES.SHIPMENTS);
        },
      }
    );
  };

  const useUploadDocument = (onUploadSuccess: (shipmentDocument: ShipmentDocument) => void) =>
    useMutation({
      mutationKey: DocumentKeys.upload(),
      mutationFn: (shipmentDocument: ShipmentDocument) =>
        ShipmentDocumentService.create(shipmentDocument),
      onSuccess: (response) => {
        if (response && response?.data) {
          onUploadSuccess(response?.data);
        }
      },
    });

  const useUploadMultipleDocuments = (
    onUploadSuccess: (shipmentDocument: ShipmentDocument) => void
  ) =>
    useMutation({
      mutationKey: DocumentKeys.uploadMultiple(),
      mutationFn: (payload: ShipmentDocument & { files: FileInput[] }) =>
        ShipmentDocumentService.createMultiple(payload),
      onSuccess: (response) => {
        if (response && response?.data) {
          onUploadSuccess(response?.data);
        }
      },
    });

  const useClearDocument = (onUploadSuccess: (shipmentDocument: ShipmentDocument) => void) =>
    useMutation({
      mutationKey: DocumentKeys.clear(),
      mutationFn: (shipmentDocument: ShipmentDocument) =>
        ShipmentDocumentService.clear(shipmentDocument.id, shipmentDocument),
      onSuccess: (response) => {
        if (response && response?.data) {
          onUploadSuccess(response?.data);
        }
      },
    });

  const useRemoveArchive = (
    onRemoveSuccess: (documentId: number, archiveId: number) => void
  ) =>
    useMutation({
      mutationKey: DocumentKeys.removeArchive(),
      mutationFn: ({ documentId, archiveId }: { documentId: number; archiveId: number }) =>
        ShipmentDocumentService.removeArchive(documentId, archiveId),
      onSuccess: (_response, variables) => {
        onRemoveSuccess(variables.documentId, variables.archiveId);
      },
    });

  const getMarineStatus = (
    setContainerStatus: (status?: ContainerStatus) => void,
    setContainerPosition: (position?: ContainerPosition) => void
  ) => {
    return useQuery(
      [`container.status.${shipment?.id}`, shipment?.marineTrafficId],
      () =>
        MarineTrafficService.status(
          shipment?.marineTrafficId || shipment?.airwayBill || "",
          shipment?.shipmentType
        ),
      {
        enabled:
          !!shipment?.shipmentTrackingId &&
          (shipment?.shipmentType === ShipmentType.SEA ||
            shipment?.shipmentType === ShipmentType.AIR) &&
          shipment?.shipmentTracking?.shipmentStatus?.toLowerCase() !==
            ShipmentStatus.WAITING_FOR_UPDATE.toLowerCase(),
        refetchOnWindowFocus: false,
        cacheTime: 0,
        retry: false,
        onSuccess: (response) => {
          if (!response.data) setContainerStatus(undefined);
          if (response && response.data) {
            setContainerStatus(response.data);
            const position = {
              TIMESTAMP: shipment?.shipmentTracking?.updatedAt,
              LAT: shipment?.shipmentTracking?.vesselPosition?.latitude ?? "",
              LON: shipment?.shipmentTracking?.vesselPosition?.longitude ?? "",
            } as unknown as ContainerPosition;

            setContainerPosition(position);
          }
        },
      }
    );
  };

  return {
    getAllShipments,
    getAllShipmentsCount,
    getShipmentById,
    getExportShipmentsById,
    getShipmentsById,
    getShipmentHistories,
    getReceivedProducts,
    useImportShipment,
    useSaveOldShipment,
    useSaveShipment,
    useUpdateShipmentFromMarineStatus,
    useExportShipment,
    useCancelShipment,
    useDeleteShipmentContainer,
    useUploadDocument,
    useUploadMultipleDocuments,
    useClearDocument,
    useRemoveArchive,
    getMarineStatus,
  };
};

export default useActionShipment;
